/**
 * BillplzPaymentReturnView — Halaman selepas user redirect balik dari Billplz.
 *
 * Sumber kebenaran status = dokumen `orders/{orderId}` (ditulis oleh webhook server,
 * BUKAN parameter redirect yang boleh dipalsukan). Kita "dengar" (onSnapshot) doc tu:
 *   - status 'paid'   → papar berjaya, license dah aktif
 *   - status 'pending'→ webhook mungkin belum sampai; tunggu + papar "sedang sahkan"
 *   - status 'failed' → papar gagal
 * Rules Firestore benarkan buyer baca order sendiri.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { CheckCircle2, Loader2, XCircle, Clock, ArrowRight, RefreshCw, MessageCircle } from 'lucide-react';

export default function BillplzPaymentReturnView() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const orderId = searchParams.get('order');

    const [status, setStatus] = useState('loading'); // loading | pending | paid | failed | notfound
    const [order, setOrder] = useState(null);
    const [waitedLong, setWaitedLong] = useState(false);

    // Mesti log masuk untuk baca order sendiri
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            if (!user) navigate('/login');
        });
        return unsub;
    }, [navigate]);

    // Dengar status order secara langsung
    useEffect(() => {
        if (!orderId) { setStatus('notfound'); return; }
        const unsub = onSnapshot(
            doc(db, 'orders', orderId),
            (snap) => {
                if (!snap.exists()) { setStatus('notfound'); return; }
                const d = snap.data();
                setOrder(d);
                if (d.status === 'paid') setStatus('paid');
                else if (d.status === 'failed') setStatus('failed');
                else setStatus('pending');
            },
            (err) => { console.error('[BillplzReturn] snapshot error:', err); setStatus('pending'); }
        );
        return unsub;
    }, [orderId]);

    // Selepas ~25 saat masih belum 'paid' → papar nota "sedang proses"
    useEffect(() => {
        const t = setTimeout(() => setWaitedLong(true), 25000);
        return () => clearTimeout(t);
    }, []);

    const amountRM = order ? `RM${((order.amountSen || 0) / 100).toFixed(2)}` : '';

    const Card = ({ children }) => (
        <div className="min-h-[70vh] flex items-center justify-center px-4">
            <div className="max-w-md w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 shadow-xl text-center">
                {children}
            </div>
        </div>
    );

    if (status === 'loading') {
        return (
            <Card>
                <Loader2 className="w-10 h-10 text-slate-400 animate-spin mx-auto mb-4" />
                <p className="text-sm text-slate-500 dark:text-slate-400">Memuatkan status pembayaran…</p>
            </Card>
        );
    }

    if (status === 'paid') {
        return (
            <Card>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-emerald-500/30">
                    <CheckCircle2 className="w-9 h-9 text-white" />
                </div>
                <h1 className="text-xl font-extrabold text-slate-800 dark:text-white mb-2">Pembayaran Berjaya</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                    Terima kasih! Langganan Premium anda telah diaktifkan secara automatik.
                </p>
                {amountRM && (
                    <p className="text-xs text-slate-400 mb-6">Jumlah dibayar: <span className="font-semibold text-slate-600 dark:text-slate-300">{amountRM}</span></p>
                )}
                <button
                    onClick={() => navigate('/workspace')}
                    className="w-full py-3.5 rounded-xl font-extrabold text-sm bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/40 hover:opacity-95 active:scale-[0.98] transition flex items-center justify-center gap-2"
                >
                    Mula Guna SESAAT <ArrowRight size={16} />
                </button>

                {/* Jemputan Group Support Premium (WhatsApp) */}
                <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-700 text-left">
                    <p className="text-sm font-bold text-slate-800 dark:text-white mb-1 flex items-center gap-1.5">
                        <span>🎉</span> Langkah seterusnya
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
                        Sertai <span className="font-semibold text-slate-700 dark:text-slate-300">Group Support Premium</span> kami untuk bantuan keutamaan, tips terkini &amp; kemas kini produk terus daripada pasukan SESAAT.
                    </p>
                    <a
                        href="https://chat.whatsapp.com/GJtccD9aKu12gWYgFtxsGr"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-3.5 rounded-xl font-extrabold text-sm bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-green-500/30 active:scale-[0.98] transition flex items-center justify-center gap-2"
                    >
                        <MessageCircle size={16} /> Sertai Group Support Premium
                    </a>
                </div>
            </Card>
        );
    }

    if (status === 'failed') {
        return (
            <Card>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-red-500 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-rose-500/30">
                    <XCircle className="w-9 h-9 text-white" />
                </div>
                <h1 className="text-xl font-extrabold text-slate-800 dark:text-white mb-2">Pembayaran Tidak Berjaya</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                    Pembayaran anda tidak dapat diproses. Tiada apa-apa yang dicaj. Sila cuba semula.
                </p>
                <button
                    onClick={() => navigate('/pricing')}
                    className="w-full py-3.5 rounded-xl font-extrabold text-sm bg-slate-800 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 active:scale-[0.98] transition"
                >
                    Cuba Semula
                </button>
            </Card>
        );
    }

    if (status === 'notfound') {
        return (
            <Card>
                <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-5">
                    <XCircle className="w-9 h-9 text-slate-400" />
                </div>
                <h1 className="text-xl font-extrabold text-slate-800 dark:text-white mb-2">Pesanan Tidak Dijumpai</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                    Kami tidak menemui pesanan ini. Jika anda telah membuat pembayaran, sila hubungi admin.
                </p>
                <button
                    onClick={() => navigate('/pricing')}
                    className="w-full py-3.5 rounded-xl font-extrabold text-sm bg-slate-800 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 active:scale-[0.98] transition"
                >
                    Kembali ke Harga
                </button>
            </Card>
        );
    }

    // status === 'pending'
    return (
        <Card>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-amber-500/30">
                <Clock className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-xl font-extrabold text-slate-800 dark:text-white mb-2">Mengesahkan Pembayaran…</h1>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Sila tunggu sebentar</span>
            </div>
            {waitedLong ? (
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
                    Pengesahan mengambil masa lebih lama daripada biasa. Jika anda sudah membayar, langganan
                    anda akan aktif dalam beberapa minit — anda boleh tutup halaman ini dengan selamat, atau
                    hubungi admin jika ia masih belum aktif selepas 10 minit.
                </p>
            ) : (
                <p className="text-xs text-slate-400 mb-6">Jangan tutup halaman ini sehingga selesai.</p>
            )}
            <button
                onClick={() => window.location.reload()}
                className="w-full py-3 rounded-xl font-semibold text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition flex items-center justify-center gap-2"
            >
                <RefreshCw size={14} /> Semak Semula
            </button>
        </Card>
    );
}
